import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Container, Dropdown, DropdownButton, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { PencilIcon } from '@primer/octicons-react'
import { BiXCircle } from "react-icons/bi";
import { axiosAuth_PLATFORM_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import { translateAPIError } from '../utils/apiUtils';
import { formatDate } from '../utils/timeUtils';
import { validateEmail } from '../utils/uiUtils';


const AppAdminDetailsView = (props) => {

  // Utilities
  const { t } = useTranslation(['common', 'custom']);
  const navigate = useNavigate();

  // Initialize with actual JSON obj.  If not, will get error.
  const [appAttrs, setAppAttrs] = useState({
    appId: '',
    appName: '',
    description: '',
    vendor: '',
    status: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    updatedBy: '',
    timeUpdated: '',
    createdBy: '',
    timeCreated: ''
  });


  // UI Functions
  const { objDetails } = props;

  // Initialize state
  useEffect(() => {

    if (objDetails) {
      setAppAttrs(objDetails);
    }
  }, [objDetails]);

  // View vs Edit Modes
  const [editMode, setEditMode] = useState(false);

  const [errMsg, setErrMsg] = useState('');

  const appStatusList = [
    { key: "ACTIVE",     value: "ACTIVE" },
    { key: "SUSPENDED",  value: "SUSPENDED" },
    { key: "EXPIRED",    value: "EXPIRED" }
  ];

  const handleEditAttrs = (e) => {
    e.preventDefault();
    setEditMode(true);
  }

  const onAttrsInputChange = (e) => {
    setAppAttrs({ ...appAttrs, [e.target.name]: e.target.value })
  }

  const onAppStatusSelect = (key, event) => {
    setAppAttrs({ ...appAttrs, status: key })
  };

  const handleGenKeyNSecret = () => {
    new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_PLATFORM_API.get("gen-random-key-secret/128");

        resolve(res.data);

        if (res.status === 200) {
          // Deep copy
          let tmpAttrs = JSON.parse(JSON.stringify(appAttrs));

          tmpAttrs.appKey    = res.data.key;
          tmpAttrs.appSecret = res.data.secret;

          setAppAttrs(tmpAttrs);
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });

    return;
  }

  const handleClearKeyNSecret = () => {
    // Deep copy
    let tmpAttrs = JSON.parse(JSON.stringify(appAttrs));

    tmpAttrs.appKey    = ""
    tmpAttrs.appSecret = ""

    setAppAttrs(tmpAttrs);
  }


  const handleEditCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setAppAttrs(props.objDetails);

    setEditMode(false);
  }

  const handleEditSubmit = (e) => {
    e.preventDefault();

    // Validate fields
    if (!appAttrs.name || appAttrs.name.length === 0) {
      setErrMsg(t("I18N_ERR_Need_App_name"));
    } else if (!appAttrs.vendor || appAttrs.vendor.length === 0) {
      setErrMsg(t("I18N_ERR_Need_App_vendor"));
    } else if (!appAttrs.status || appAttrs.status.length === 0) {
      setErrMsg(t("I18N_ERR_Need_App_status"));
    } else if (!appAttrs.contactName || appAttrs.contactName.length === 0) {
      setErrMsg(t("I18N_ERR_Need_Contact_name"));
    } else if (!appAttrs.contactEmail || appAttrs.contactEmail.length === 0) {
      setErrMsg(t("I18N_ERR_Need_Contact_email"));
    } else if (!validateEmail(appAttrs.contactEmail)) {
      setErrMsg(t("I18N_ERR_Bad_Email"));
    } else if (!appAttrs.contactPhone || appAttrs.contactPhone.length === 0) {
      setErrMsg(t("I18N_ERR_Need_Contact_phone"));
    } else {
      const reqBody = appAttrs;

      return new Promise(async (resolve, reject) => {
        try {
          const res = await axiosAuth_PLATFORM_API.put("apps/" + appAttrs.id, reqBody);

          resolve(res.data);

          if (res.status === 200) {

            // Restore UI to viewing mode
            setEditMode(false);
          }
        } catch (error) {
          if (!error.message || (error.message !== 'IGNORE')) {
            setErrMsg(t(translateAPIError(error)));
          }
        }
      });
    }
  }

  const handleClose = (e) => {
    e.preventDefault();

    // Return to list
    navigate(-1);
  }


  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_App_Details")}
        </span>
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_App_info")}
            </span>
            {editMode ?
              null
              :
              <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditAttrs}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
            }
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editMode ?
            <Container>
              <form>
                <Row className={classes.admin_pages__field_label}>
                  <Col>{t("I18N_App_id")}:</Col>
                  <Col>{t("I18N_App_name")}:</Col>
                  <Col>{t("I18N_Vendor")}: *</Col>
                  <Col>{t("I18N_Status")}: *</Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>{appAttrs.id}</Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="name" value={appAttrs.name}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="vendor" value={appAttrs.vendor}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={t("common:I18N_" + appAttrs.status)}
                      onSelect={onAppStatusSelect}>
                      {appStatusList.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                            {t("common:I18N_" + item.value)}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col>{t("I18N_Description")}:</Col>
                  <Col>{t("I18N_Contact_name")}: *</Col>
                  <Col>{t("I18N_Contact_email")}: *</Col>
                  <Col>{t("I18N_Contact_phone")}: *</Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <textarea className={classes.admin_pages__field_editable} name="description" value={appAttrs.description}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="contactName" value={appAttrs.contactName}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="contactEmail" value={appAttrs.contactEmail}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="contactPhone" value={appAttrs.contactPhone}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col></Col>
                  <Col>{t("I18N_App_key")}:</Col>
                  <Col>{t("I18N_App_secret")}:</Col>
                  <Col></Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleGenKeyNSecret}>
                      {t("I18N_Gen_key_n_secret")}
                    </Button>
                    <OverlayTrigger overlay={<Tooltip id="tooltip-del_dp" style={{marginLeft:'15px', fontSize:'11px'}}>{t("I18N_TIP_Clear")}</Tooltip>}>
                      <span style={{marginLeft:'10px', cursor:'pointer', color:'gray'}} onClick={() => handleClearKeyNSecret()}><BiXCircle size={16} /></span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <textarea className={classes.admin_pages__field_editable} disabled={true} style={{minWidth: '100%', minHeight:'100px', backgroundColor:'white'}}
                              name="contactEmail" value={appAttrs.appKey}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <textarea className={classes.admin_pages__field_editable} disabled={true} style={{minWidth: '100%', minHeight:'100px', backgroundColor:'white'}}
                              name="contactPhone" value={appAttrs.appSecret}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col></Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                  <span>{t("I18N_Req_fields")}</span>
                </Row>

                <Row>
                  <div display='flex' style={{marginTop:'15px'}}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleEditCancel}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleEditSubmit}>
                      {t("I18N_Save")}
                    </Button>
                    <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                      {errMsg}
                    </span>
                  </div>
                </Row>
              </form>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label}>
                <Col>{t("I18N_App_id")}:</Col>
                <Col>{t("I18N_App_name")}:</Col>
                <Col>{t("I18N_Vendor")}:</Col>
                <Col>{t("I18N_Status")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{appAttrs.id}</Col>
                <Col>{appAttrs.name}</Col>
                <Col>{appAttrs.vendor}</Col>
                <Col>{t("common:I18N_"  + appAttrs.status)}</Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Description")}:</Col>
                <Col>{t("I18N_Contact_name")}:</Col>
                <Col>{t("I18N_Contact_email")}:</Col>
                <Col>{t("I18N_Contact_phone")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{appAttrs.description}</Col>
                <Col>{appAttrs.contactName}</Col>
                <Col>{appAttrs.contactEmail}</Col>
                <Col>{appAttrs.contactPhone}</Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col>{t("I18N_App_key")}:</Col>
                  <Col>{t("I18N_App_secret")}:</Col>
                  <Col></Col>
                  <Col></Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col style={{maxHeight:'120px', overflow:'auto', flexWrap:'wrap', wordWrap:'break-word'}}>
                  {appAttrs.appKey}
                </Col>
                <Col style={{maxHeight:'120px', overflow:'auto', flexWrap:'wrap', wordWrap:'break-word'}}>
                  {appAttrs.appSecret}
                </Col>
                  <Col></Col>
                  <Col></Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col>{t("I18N_Created_by")}:</Col>
                  <Col>{t("I18N_Time_created")}:</Col>
                  <Col>{t("I18N_Updated_by")}:</Col>
                  <Col>{t("I18N_Time_updated")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{appAttrs.creatorUsername}</Col>
                <Col>{formatDate(appAttrs.timeCreated)}</Col>
                <Col>{appAttrs.updaterUsername}</Col>
                <Col>{formatDate(appAttrs.timeUpdated)}</Col>
              </Row>
            </Container>
          }
        </div>


        <hr className="solid" style={{ marginTop: '30px' }}></hr>

        <div className={classes.admin_pages__page_subtitle} >
          <Form.Group as={Row} className="mb-2 mt-3">
            <div style={{ display: "flex" }}>
              <Button size="sm" variant="outline-secondary" className="mb-3"
                style={{ marginLeft:'0px', fontSize:'12px', fontWeight:'300' }}
                onClick={handleClose}>
                {t("I18N_Close")}
              </Button>
{/*
              <span className={classes.admin_pages__field_label} style={{ color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                {errMsg}
              </span>
*/}
            </div>
          </Form.Group>
        </div>

      </Card>
    </>
  );
}

export default AppAdminDetailsView;
